import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AddCircle,
  Block,
  CheckCircle,
  Edit,
  History,
  ListAlt,
  MonetizationOn,
  Refresh,
  Search,
  WarningAmber,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import API from '../../services/api';
import { addTab, mergeFlow } from '../../tabs/tabsSlice';
import MainText from '../../components/MainText';
import CustomModal from '../../components/CustomModal';
import CustomSnackBar from '../../components/CustomSnackBar';
import CleanningButton from '../../components/CleanningButton';
import CustomSwitch from '../../components/CustomSwitch';
import PriceManagerModal from './PriceManagerModal';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(value || 0));

const formatShortDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('es-GT');
};

const todayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PRODUCT_STATUS = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

const DEFAULT_FILTERS = {
  search: '',
  status: PRODUCT_STATUS.ACTIVE,
  supplierId: 'ALL',
  categoryId: 'ALL',
};

function PriceHistoryContent({ rows, loading }) {
  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    return list.sort((a, b) => new Date(b?.dateBegin || 0) - new Date(a?.dateBegin || 0));
  }, [rows]);

  if (loading) {
    return <Typography variant="body2">Cargando historial...</Typography>;
  }

  if (!sortedRows.length) {
    return <Typography variant="body2">Sin historial de precios</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {sortedRows.map((price) => {
        const priceTypeLabel = price.priceType?.name || price.priceType || 'Sin tipo';
        return (
          <Paper key={price.id || `${price.price}-${price.dateBegin}`} variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatMoney(price.price)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {priceTypeLabel}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Desde {formatShortDate(price.dateBegin)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {price.dateEnd ? `Hasta ${formatShortDate(price.dateEnd)}` : 'Sin fecha fin'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

export default function ListProduct() {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [subtitleTick, setSubtitleTick] = useState(Date.now());
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [pricesOpen, setPricesOpen] = useState(false);
  const [pricesProduct, setPricesProduct] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [batchPricesOpen, setBatchPricesOpen] = useState(false);
  const [batchAction, setBatchAction] = useState('create');
  const [batchMode, setBatchMode] = useState('homogeneous');
  const [batchPriceTypeId, setBatchPriceTypeId] = useState('');
  const [batchPriceValue, setBatchPriceValue] = useState('');
  const [batchMixedValues, setBatchMixedValues] = useState({});
  const [batchDateBegin, setBatchDateBegin] = useState(todayLocal());
  const [batchComment, setBatchComment] = useState('');
  const [batchSaving, setBatchSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('info');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const restoredFiltersRef = useRef(false);

  const dispatch = useDispatch();
  const savedFilters = useSelector((state) => state.tabs.flows.listProduct || null);
  const navigate = useNavigate();
  const theme = useTheme();

  const showSnack = useCallback((message, severity = 'info') => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  }, []);

  useEffect(() => {
    dispatch(addTab({ label: 'Productos', path: '/orders/product/all-products' }));
  }, [dispatch]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setSubtitleTick(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (restoredFiltersRef.current) return;
    restoredFiltersRef.current = true;

    if (savedFilters && typeof savedFilters === 'object') {
      setFilters((prev) => ({
        ...prev,
        ...savedFilters,
        supplierId:
          savedFilters.supplierId == null || savedFilters.supplierId === ''
            ? 'ALL'
            : String(savedFilters.supplierId),
        categoryId:
          savedFilters.categoryId == null || savedFilters.categoryId === ''
            ? 'ALL'
            : String(savedFilters.categoryId),
      }));

      if (typeof savedFilters.search === 'string') {
        setDebouncedSearch(savedFilters.search.trim().toLowerCase());
      }
    }
  }, [savedFilters]);

  useEffect(() => {
    dispatch(
      mergeFlow({
        name: 'listProduct',
        data: {
          ...filters,
          supplierId:
            filters.supplierId == null || filters.supplierId === ''
              ? 'ALL'
              : String(filters.supplierId),
          categoryId:
            filters.categoryId == null || filters.categoryId === ''
              ? 'ALL'
              : String(filters.categoryId),
        },
      })
    );
  }, [dispatch, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch((filters.search || '').trim().toLowerCase());
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data } = await API.get('suppliers/minimal/');
        if (!mounted) return;
        const items = Array.isArray(data) ? data : data?.results || [];
        setSupplierOptions(items);
      } catch (error) {
        console.error('Error cargando proveedores', error);
        if (mounted) {
          setSupplierOptions([]);
        }
      }

      try {
        const { data } = await API.get('catalogs/categories/');
        if (!mounted) return;
        const items = Array.isArray(data) ? data : data?.results || [];
        setCategoryOptions(items);
      } catch (error) {
        console.error('Error cargando categorias', error);
        if (mounted) {
          setCategoryOptions([]);
        }
      }

      try {
        const { data } = await API.get('catalogs/price-types/');
        if (!mounted) return;
        const items = Array.isArray(data) ? data : data?.results || [];
        setPriceTypes(items);
      } catch (error) {
        console.error('Error cargando tipos de precio', error);
        if (mounted) {
          setPriceTypes([]);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};

      if (filters.status === PRODUCT_STATUS.ACTIVE) {
        params.active = true;
      } else if (filters.status === PRODUCT_STATUS.INACTIVE) {
        params.active = false;
      }

      if (filters.supplierId !== 'ALL' && filters.supplierId !== '') {
        const parsedSupplier = Number(filters.supplierId);
        if (!Number.isNaN(parsedSupplier)) {
          params.supplier_id = parsedSupplier;
        }
      }

      if (filters.categoryId !== 'ALL' && filters.categoryId !== '') {
        const parsedCategory = Number(filters.categoryId);
        if (!Number.isNaN(parsedCategory)) {
          params.category_id = parsedCategory;
        }
      }

      const { data } = await API.get('product/', { params });
      const rows = Array.isArray(data) ? data : data?.results || [];
      setAllProducts(rows);
      setLastFetchAt(new Date());
    } catch (error) {
      console.error('Error cargando productos', error);
      setAllProducts([]);
      showSnack('No se pudo cargar el listado de productos', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters.categoryId, filters.status, filters.supplierId, showSnack]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchHistory = useCallback(async (productId) => {
    if (!productId) {
      setHistoryRows([]);
      return;
    }

    setHistoryLoading(true);
    try {
      const { data } = await API.get('price/', { params: { product: productId } });
      const rows = Array.isArray(data) ? data : data?.results || [];
      setHistoryRows(rows);
    } catch (error) {
      console.error('Error cargando historial de precios', error);
      setHistoryRows([]);
      showSnack('No se pudo cargar el historial de precios', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showSnack]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setDebouncedSearch('');
  }, []);

  const handleOpenPrices = useCallback((product) => {
    setPricesProduct(product);
    setPricesOpen(true);
  }, []);

  const handleClosePrices = useCallback(() => {
    setPricesOpen(false);
    setPricesProduct(null);
  }, []);

  const handleOpenHistory = useCallback((product) => {
    setHistoryProduct(product);
    setHistoryOpen(true);
    fetchHistory(product?.id);
  }, [fetchHistory]);
  void handleOpenHistory;

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setHistoryProduct(null);
    setHistoryRows([]);
  };

  const handleToggleProduct = useCallback(async (product) => {
    if (!product?.id) return;

    const nextActive = !product.is_active;
    const confirmationMessage = nextActive
      ? '¿Activar este producto?'
      : '¿Desactivar este producto?';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      await API.patch(`product/${product.id}/`, { is_active: nextActive });
      await fetchData();
      showSnack(nextActive ? 'Producto activado' : 'Producto desactivado', 'success');
    } catch (error) {
      console.error('Error actualizando producto', error);
      showSnack('No se pudo actualizar el producto', 'error');
    }
  }, [fetchData, showSnack]);

  const filteredProducts = useMemo(() => {
    return allProducts.filter((row) => {
      const supplierId = String(row?.supplier?.id || row?.supplier_id || '');
      const categoryId = String(row?.category?.id || row?.category_id || '');
      const matchesSupplier =
        filters.supplierId === PRODUCT_STATUS.ALL ||
        filters.supplierId === 'ALL' ||
        supplierId === String(filters.supplierId);
      const matchesCategory =
        filters.categoryId === PRODUCT_STATUS.ALL ||
        filters.categoryId === 'ALL' ||
        categoryId === String(filters.categoryId);
      const matchesStatus =
        filters.status === PRODUCT_STATUS.ALL
          ? true
          : filters.status === PRODUCT_STATUS.ACTIVE
            ? Boolean(row?.is_active)
            : !row?.is_active;

      if (!matchesSupplier || !matchesCategory || !matchesStatus) {
        return false;
      }

      if (!debouncedSearch) {
        return true;
      }

      const haystack = [
        row?.code,
        row?.description,
        row?.supplier?.companyName,
        row?.supplier?.legalName,
        row?.category?.name,
        row?.prformat?.name,
        ...(row?.prices || []).map((price) => price?.priceType?.name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(debouncedSearch);
    });
  }, [allProducts, debouncedSearch, filters.categoryId, filters.status, filters.supplierId]);

  const handleEditProduct = useCallback((productId) => {
    if (!productId) return;
    navigate(`/orders/product/edit-products/${productId}`);
  }, [navigate]);

  const subtitleText = useMemo(() => {
    const minutesFromLastFetch = lastFetchAt
      ? Math.max(0, Math.floor((subtitleTick - lastFetchAt.getTime()) / 60000))
      : 0;

    return `${filteredProducts.length} productos · última actualización hace ${minutesFromLastFetch} min`;
  }, [filteredProducts.length, lastFetchAt, subtitleTick]);

  useEffect(() => {
    setSelectedProductIds((prev) => prev.filter((id) => allProducts.some((row) => row.id === id)));
  }, [allProducts]);

  const totalPages = useMemo(() => {
    if (!filteredProducts.length) return 1;
    return Math.ceil(filteredProducts.length / pageSize);
  }, [filteredProducts.length, pageSize]);

  useEffect(() => {
    setPage((currentPage) => {
      const maxPage = Math.max(0, totalPages - 1);
      return currentPage > maxPage ? maxPage : currentPage;
    });
  }, [totalPages]);

  const paginatedProducts = useMemo(() => {
    const start = page * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, page, pageSize]);

  const paginationLabel = useMemo(() => {
    if (!filteredProducts.length) {
      return '0 productos · mostrando 0-0';
    }

    const start = page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, filteredProducts.length);
    return `${filteredProducts.length} productos · mostrando ${start}-${end}`;
  }, [filteredProducts.length, page, pageSize]);

  const visiblePageButtons = useMemo(() => {
    const maxButtons = 5;
    let start = Math.max(0, page - 2);
    let end = Math.min(totalPages, start + maxButtons);

    if (end - start < maxButtons) {
      start = Math.max(0, end - maxButtons);
    }

    return Array.from({ length: end - start }, (_, index) => start + index);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.status, filters.supplierId, filters.categoryId]);

  const selectedProducts = useMemo(
    () => allProducts.filter((row) => selectedProductIds.includes(row.id)),
    [allProducts, selectedProductIds]
  );

  const getActivePriceForType = useCallback((product, priceTypeId) => {
    const expectedType = String(priceTypeId || '');
    return (product?.prices || []).find((price) => (
      Boolean(price?.is_active) &&
      String(price?.priceType?.id || price?.priceType_id || price?.priceType || '') === expectedType
    )) || null;
  }, []);

  const currentBatchRows = useMemo(() => {
    if (!batchPriceTypeId) return [];
    return selectedProducts.map((product) => ({
      product,
      price: getActivePriceForType(product, batchPriceTypeId),
    }));
  }, [batchPriceTypeId, getActivePriceForType, selectedProducts]);

  const batchSelectionMeta = useMemo(() => {
    if (selectedProducts.length < 2) {
      return { ready: false, message: 'Selecciona al menos dos productos.' };
    }

    const supplierIds = new Set(
      selectedProducts
        .map((product) => String(product?.supplier?.id || product?.supplier_id || ''))
        .filter(Boolean)
    );
    if (supplierIds.size > 1) {
      return {
        ready: false,
        message: 'La operacion en lote solo esta disponible para productos del mismo proveedor.',
      };
    }

    if (!batchPriceTypeId) {
      return { ready: false, message: 'Selecciona el tipo de precio a trabajar.' };
    }

    if (batchAction !== 'create' && currentBatchRows.some(({ price }) => !price)) {
      return {
        ready: false,
        message: 'Todos los productos seleccionados deben tener un precio activo del tipo elegido.',
      };
    }

    const currentAmounts = new Set(
      currentBatchRows.map(({ price }) => Number(price?.price || 0).toFixed(2))
    );
    if (batchAction !== 'create' && currentAmounts.size > 1) {
      return {
        ready: false,
        message: 'Solo se permite actualizar o eliminar en lote si coinciden proveedor y costo actual.',
      };
    }

    return {
      ready: true,
      message: '',
      supplierLabel:
        selectedProducts[0]?.supplier?.companyName ||
        selectedProducts[0]?.supplier?.legalName ||
        '-',
      currentAmount: batchAction === 'create' ? null : Number(currentBatchRows[0]?.price?.price || 0),
    };
  }, [batchAction, batchPriceTypeId, currentBatchRows, selectedProducts]);

  useEffect(() => {
    if (!batchPricesOpen || !batchPriceTypeId) return;
    setBatchMixedValues(
      Object.fromEntries(
        currentBatchRows.map(({ product, price }) => [product.id, String(price?.price || '')])
      )
    );
  }, [batchPriceTypeId, batchPricesOpen, currentBatchRows]);

  const toggleProductSelection = useCallback((productId) => {
    setSelectedProductIds((prev) => (
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    ));
  }, []);

  const visibleProductIds = useMemo(
    () => paginatedProducts.map((row) => row.id),
    [paginatedProducts]
  );

  const allVisibleSelected = useMemo(
    () => visibleProductIds.length > 0 && visibleProductIds.every((id) => selectedProductIds.includes(id)),
    [selectedProductIds, visibleProductIds]
  );

  const someVisibleSelected = useMemo(
    () => visibleProductIds.some((id) => selectedProductIds.includes(id)),
    [selectedProductIds, visibleProductIds]
  );

  const handleToggleVisibleProducts = useCallback(() => {
    setSelectedProductIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleProductIds.includes(id));
      }

      const next = new Set(prev);
      visibleProductIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [allVisibleSelected, visibleProductIds]);

  const resetBatchState = useCallback(() => {
    setBatchAction('create');
    setBatchMode('homogeneous');
    setBatchPriceTypeId('');
    setBatchPriceValue('');
    setBatchMixedValues({});
    setBatchDateBegin(todayLocal());
    setBatchComment('');
    setBatchSaving(false);
  }, []);

  const handleCloseBatchPrices = useCallback(() => {
    setBatchPricesOpen(false);
    resetBatchState();
  }, [resetBatchState]);

  const handleOpenBatchPrices = useCallback(() => {
    if (selectedProductIds.length < 2) {
      showSnack('Selecciona al menos dos productos para trabajar precios en lote.', 'warning');
      return;
    }
    resetBatchState();
    setBatchPricesOpen(true);
  }, [resetBatchState, selectedProductIds.length, showSnack]);

  const handleBatchMixedValueChange = useCallback((productId, value) => {
    setBatchMixedValues((prev) => ({ ...prev, [productId]: value }));
  }, []);

  const handleSubmitBatchPrices = useCallback(async () => {
    if (!batchSelectionMeta.ready) {
      showSnack(batchSelectionMeta.message, 'warning');
      return;
    }

    if (batchAction === 'update' || batchAction === 'create') {
      if (batchMode === 'homogeneous') {
        const parsedValue = Number(batchPriceValue);
        if (!parsedValue || Number.isNaN(parsedValue) || parsedValue <= 0) {
          showSnack('Ingresa un precio valido para la actualizacion homogénea.', 'warning');
          return;
        }
      } else {
        const invalidMixed = currentBatchRows.some(({ product }) => {
          const parsedValue = Number(batchMixedValues[product.id]);
          return !parsedValue || Number.isNaN(parsedValue) || parsedValue <= 0;
        });
        if (invalidMixed) {
          showSnack('Todos los montos mixtos deben ser mayores a 0.', 'warning');
          return;
        }
      }
    }

    setBatchSaving(true);
    try {
      for (const { product, price } of currentBatchRows) {
        if (price?.id && (batchAction === 'delete' || batchAction === 'create')) {
          await API.patch(`price/${price.id}/`, {
            is_active: false,
            dateEnd: todayLocal(),
          });
        }

        if (batchAction === 'update' && price?.id) {
          const nextPrice = batchMode === 'mixed'
            ? Number(batchMixedValues[product.id])
            : Number(batchPriceValue);

          await API.patch(`price/${price.id}/`, {
            price: nextPrice,
            dateBegin: batchDateBegin,
            comment: batchComment,
          });
        }

        if (batchAction === 'create') {
          const nextPrice = batchMode === 'mixed'
            ? Number(batchMixedValues[product.id])
            : Number(batchPriceValue);

          await API.post('price/', {
            price: nextPrice,
            priceType_id: Number(batchPriceTypeId),
            dateBegin: batchDateBegin,
            comment: batchComment,
            product: product.id,
          });
        }
      }

      await fetchData();
      setSelectedProductIds([]);
      handleCloseBatchPrices();
      showSnack(
        batchAction === 'delete'
          ? 'Precios desactivados en lote.'
          : batchAction === 'create'
            ? 'Nuevo costo creado en lote.'
            : 'Precios editados en lote.',
        'success'
      );
    } catch (error) {
      console.error('Error aplicando precios en lote', error);
      showSnack('No se pudo completar la operacion de precios en lote.', 'error');
    } finally {
      setBatchSaving(false);
    }
  }, [
    batchAction,
    batchDateBegin,
    batchMixedValues,
    batchMode,
    batchPriceTypeId,
    batchPriceValue,
    batchComment,
    batchSelectionMeta,
    currentBatchRows,
    fetchData,
    handleCloseBatchPrices,
    showSnack,
  ]);

  const tableLabelSx = {
    fontSize: 12,
    fontWeight: 500,
    color: '#ffffff',
    p: '11px 14px',
    textAlign: 'left',
  };

  const priceTagSx = {
    fontSize: 11,
    px: '7px',
    py: '1px',
    borderRadius: '4px',
    border: '0.5px solid',
    borderColor: 'divider',
    backgroundColor: theme.palette.background?.secondary || theme.palette.action.hover,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <MainText title="Productos" subtitle={subtitleText} icon={ListAlt} width="auto" />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {selectedProductIds.length > 1 ? (
            <Button
              variant="outlined"
              startIcon={<MonetizationOn />}
              onClick={handleOpenBatchPrices}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Precios en lote ({selectedProductIds.length})
            </Button>
          ) : null}

          <Button
            variant="contained"
            startIcon={<AddCircle />}
            onClick={() => navigate('/orders/product/create-new')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Nuevo producto
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar producto..."
          value={filters.search}
          onChange={(event) => handleFilterChange('search', event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240, flex: 1 }}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Proveedor</InputLabel>
          <Select
            value={filters.supplierId}
            onChange={(event) => handleFilterChange('supplierId', event.target.value)}
            label="Proveedor"
          >
            <MenuItem value="ALL">
              <em>Todos</em>
            </MenuItem>
            {supplierOptions.map((supplier) => (
              <MenuItem key={supplier.id} value={String(supplier.id)}>
                {supplier.companyName || `Proveedor ${supplier.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={filters.categoryId}
            onChange={(event) => handleFilterChange('categoryId', event.target.value)}
            label="Categoria"
          >
            <MenuItem value="ALL">
              <em>Todas</em>
            </MenuItem>
            {categoryOptions.map((category) => (
              <MenuItem key={category.id} value={String(category.id)}>
                {category.name || `Categoria ${category.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CustomSwitch
            name="status"
            label={filters.status === PRODUCT_STATUS.INACTIVE ? 'Inactivos' : 'Activos'}
            initialValue={filters.status !== PRODUCT_STATUS.INACTIVE}
            onChange={(_, value) => handleFilterChange('status', value ? PRODUCT_STATUS.ACTIVE : PRODUCT_STATUS.INACTIVE)}
          />
          <Button
            size="small"
            variant={filters.status === PRODUCT_STATUS.ALL ? 'contained' : 'outlined'}
            onClick={() => handleFilterChange('status', PRODUCT_STATUS.ALL)}
            sx={{ textTransform: 'none', minWidth: 70 }}
          >
            Todos
          </Button>
        </Box>

        <Tooltip title="Refrescar">
          <IconButton onClick={fetchData}>
            <Refresh />
          </IconButton>
        </Tooltip>

        <CleanningButton onClick={handleResetFilters} />
      </Box>

      <Box
        sx={{
          width: '100%',
          border: '0.5px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box component="table" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '27%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '27%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>

          <Box component="thead" sx={{ backgroundColor: '#10173c' }}>
            <Box component="tr">
              <Box component="th" sx={{ ...tableLabelSx, width: 44 }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && someVisibleSelected}
                  onChange={handleToggleVisibleProducts}
                  sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ffffff' }, '&.MuiCheckbox-indeterminate': { color: '#ffffff' } }}
                />
              </Box>
              <Box component="th" sx={tableLabelSx}>Producto</Box>
              <Box component="th" sx={tableLabelSx}>Proveedor</Box>
              <Box component="th" sx={tableLabelSx}>Precios activos</Box>
              <Box component="th" sx={tableLabelSx}>Estado</Box>
              <Box component="th" sx={{ ...tableLabelSx, textAlign: 'right' }}>Acciones</Box>
            </Box>
          </Box>

          <Box component="tbody">
            {loading ? (
                <Box component="tr">
                  <Box component="td" colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                </Box>
              ) : !paginatedProducts.length ? (
                <Box component="tr">
                  <Box component="td" colSpan={6} sx={{ p: '32px 14px', textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>
                    No hay productos para mostrar.
                  </Box>
                </Box>
              ) : (
              paginatedProducts.map((row) => {
                const activePrices = (row.prices || []).filter((price) => price.is_active === true);
                const supplierLabel = row?.supplier?.companyName || row?.supplier?.legalName || '-';
                const hasActivePrices = activePrices.length > 0;

                return (
                  <Box
                    component="tr"
                    key={row.id}
                    onDoubleClick={() => handleEditProduct(row.id)}
                    sx={{
                      borderBottom: '0.5px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      opacity: row.is_active ? 1 : 0.6,
                      transition: 'background-color 0.18s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(16,23,60,0.04)',
                      },
                      '&:last-of-type': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Box component="td" sx={{ p: '14px 8px', verticalAlign: 'middle' }}>
                      <Checkbox
                        size="small"
                        checked={selectedProductIds.includes(row.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleProductSelection(row.id)}
                      />
                    </Box>

                    <Box component="td" sx={{ p: '14px', verticalAlign: 'middle' }}>
                      <Typography sx={{ fontWeight: 500, fontSize: 13, lineHeight: 1.3 }}>
                        {row.description}
                      </Typography>
                      <Typography sx={{ mt: 0.4, fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
                        {row.code || '-'}
                      </Typography>
                    </Box>

                    <Box component="td" sx={{ p: '14px', verticalAlign: 'middle' }}>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.4 }}>
                        {supplierLabel}
                      </Typography>
                    </Box>

                    <Box component="td" sx={{ p: '14px', verticalAlign: 'middle' }}>
                      {hasActivePrices ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                          {activePrices.map((price) => (
                            <Box key={price.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 500, fontSize: 13, minWidth: 68, whiteSpace: 'nowrap' }}>
                                {formatMoney(price.price)}
                              </Typography>
                              <Box component="span" sx={priceTagSx}>
                                {price.priceType?.name || 'Sin tipo'}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'error.main' }}>
                          <WarningAmber sx={{ fontSize: 16 }} />
                          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                            Sin precio activo
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box component="td" sx={{ p: '14px', verticalAlign: 'middle' }}>
                      <Chip
                        label={row.is_active ? 'Activo' : 'Inactivo'}
                        size="small"
                        color={row.is_active ? 'success' : 'default'}
                        variant={row.is_active ? 'filled' : 'outlined'}
                      />
                    </Box>

                    <Box component="td" sx={{ p: '14px', verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', flexWrap: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={hasActivePrices ? <MonetizationOn fontSize="small" /> : <AddCircle fontSize="small" />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenPrices(row);
                          }}
                          sx={{
                            minWidth: 0,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            borderColor: hasActivePrices ? '#10173c' : 'error.main',
                            color: hasActivePrices ? '#10173c' : 'error.main',
                          }}
                        >
                          {hasActivePrices ? 'Gestionar precios' : 'Agregar precio'}
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Edit fontSize="small" />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditProduct(row.id);
                          }}
                          sx={{
                            minWidth: 0,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            color: 'text.primary',
                            borderColor: 'divider',
                          }}
                        >
                          Editar
                        </Button>

                        <Tooltip title={row.is_active ? 'Desactivar' : 'Activar'}>
                          <IconButton
                            size="small"
                            color={row.is_active ? 'error' : 'success'}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleProduct(row);
                            }}
                          >
                            {row.is_active ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            px: '14px',
            py: '10px',
            borderTop: '0.5px solid',
            borderColor: 'divider',
            backgroundColor: theme.palette.background?.secondary || theme.palette.action.hover,
          }}
        >
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {paginationLabel}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="text"
              disabled={page === 0}
              onClick={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
              sx={{ minWidth: 36, textTransform: 'none', fontSize: 12 }}
            >
              ‹
            </Button>

            {visiblePageButtons.map((pageNumber) => (
              <Button
                key={pageNumber}
                size="small"
                variant={pageNumber === page ? 'contained' : 'outlined'}
                onClick={() => setPage(pageNumber)}
                sx={{
                  minWidth: 34,
                  px: 1,
                  fontSize: 12,
                  color: pageNumber === page ? '#ffffff' : 'text.primary',
                  borderColor: 'divider',
                  backgroundColor: pageNumber === page ? '#10173c' : 'transparent',
                  '&:hover': {
                    backgroundColor: pageNumber === page ? '#10173c' : 'background.paper',
                  },
                }}
              >
                {pageNumber + 1}
              </Button>
            ))}

            <Button
              size="small"
              variant="text"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((currentPage) => Math.min(totalPages - 1, currentPage + 1))}
              sx={{ minWidth: 36, textTransform: 'none', fontSize: 12 }}
            >
              ›
            </Button>
          </Box>

          <FormControl size="small" sx={{ minWidth: 128 }}>
            <InputLabel>Registros</InputLabel>
            <Select
              value={pageSize}
              label="Registros"
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
            >
              {[20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>
                  {option} por página
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Dialog
        open={pricesOpen}
        onClose={handleClosePrices}
        maxWidth="sm"
        fullWidth
      >
        <PriceManagerModal
          open={pricesOpen}
          onClose={handleClosePrices}
          product={pricesProduct}
          onRefresh={fetchData}
        />
      </Dialog>

      <Dialog open={batchPricesOpen} onClose={handleCloseBatchPrices} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
            Gestion de precios en lote
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
            {selectedProducts.length} productos seleccionados
            {batchSelectionMeta.supplierLabel ? ` · ${batchSelectionMeta.supplierLabel}` : ''}
            {batchSelectionMeta.currentAmount != null ? ` · costo actual ${formatMoney(batchSelectionMeta.currentAmount)}` : ''}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Operacion</InputLabel>
              <Select
                value={batchAction}
                label="Operacion"
                onChange={(event) => setBatchAction(event.target.value)}
              >
                <MenuItem value="create">Crear nuevo costo</MenuItem>
                <MenuItem value="update">Editar costo</MenuItem>
                <MenuItem value="delete">Eliminar</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Tipo de precio</InputLabel>
              <Select
                value={batchPriceTypeId}
                label="Tipo de precio"
                onChange={(event) => setBatchPriceTypeId(event.target.value)}
              >
                {priceTypes.map((type) => (
                  <MenuItem key={type.id} value={String(type.id)}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {batchAction === 'update' || batchAction === 'create' ? (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Modo</InputLabel>
                <Select
                  value={batchMode}
                  label="Modo"
                  onChange={(event) => setBatchMode(event.target.value)}
                >
                  <MenuItem value="homogeneous">Homogenea</MenuItem>
                  <MenuItem value="mixed">Mixta</MenuItem>
                </Select>
              </FormControl>
            ) : null}
          </Box>

          {!batchSelectionMeta.ready ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: 'warning.main',
                backgroundColor: (muiTheme) => muiTheme.palette.warning.light,
              }}
            >
              <Typography sx={{ fontSize: 12, color: 'warning.dark' }}>
                {batchSelectionMeta.message}
              </Typography>
            </Paper>
          ) : null}

          {batchAction === 'update' || batchAction === 'create' ? (
            <>
              {batchMode === 'homogeneous' ? (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Nuevo precio"
                    type="number"
                    value={batchPriceValue}
                    onChange={(event) => setBatchPriceValue(event.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    sx={{ minWidth: 180 }}
                  />

                  <TextField
                    size="small"
                    label="Fecha inicio"
                    type="date"
                    value={batchDateBegin}
                    onChange={(event) => setBatchDateBegin(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 180 }}
                  />

                  <TextField
                    size="small"
                    label="Observacion"
                    value={batchComment}
                    onChange={(event) => setBatchComment(event.target.value)}
                    sx={{ minWidth: 220, flex: 1 }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      label="Fecha inicio"
                      type="date"
                      value={batchDateBegin}
                      onChange={(event) => setBatchDateBegin(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 180 }}
                    />
                    <TextField
                      size="small"
                      label="Observacion"
                      value={batchComment}
                      onChange={(event) => setBatchComment(event.target.value)}
                      sx={{ minWidth: 220, flex: 1 }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    Montos por producto
                  </Typography>
                  {currentBatchRows.map(({ product }) => (
                    <Box
                      key={`batch-product-${product.id}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) 140px',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {product.description}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
                          {product.code || '-'}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        value={batchMixedValues[product.id] || ''}
                        onChange={(event) => handleBatchMixedValueChange(product.id, event.target.value)}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Se desactivara el precio activo del tipo seleccionado en todos los productos elegibles.
            </Typography>
          )}

          <Divider />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              Productos incluidos
            </Typography>
            {selectedProducts.map((product) => (
              <Box
                key={`selected-product-${product.id}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  alignItems: 'center',
                  border: '0.5px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  px: 1.25,
                  py: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                    {product.description}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
                    {product.code || '-'}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {getActivePriceForType(product, batchPriceTypeId)
                    ? formatMoney(getActivePriceForType(product, batchPriceTypeId)?.price || 0)
                    : 'Sin vigente'}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseBatchPrices} disabled={batchSaving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitBatchPrices}
            disabled={batchSaving || !batchSelectionMeta.ready}
            sx={{ textTransform: 'none' }}
          >
            {batchSaving ? 'Aplicando...' : batchAction === 'delete' ? 'Eliminar en lote' : batchAction === 'create' ? 'Crear nuevo costo' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomModal
        open={historyOpen}
        handleClose={handleCloseHistory}
        setOpen={setHistoryOpen}
        title={`Historial de precios${historyProduct?.description ? ` · ${historyProduct.description}` : ''}`}
        icon={History}
        width="520px"
        component={PriceHistoryContent}
        componentProps={{ rows: historyRows, loading: historyLoading }}
      />

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMessage} severity={snackSeverity} />
    </>
  );
}
